/**
 * Returns true if the given origin matches any of the provided patterns.
 *
 * Patterns can be:
 * - Exact: `https://brand.com`
 * - Wildcard subdomain: `https://*.brand.com`
 *
 * Comparison is case-insensitive. Returns false when patterns is empty.
 */
const WILDCARD_ORIGIN_PATTERN = /^(https?):\/\/\*\.([^/:]+)(?::(\d+))?$/

const normalizeOrigin = (value: string): string | null => {
  try {
    return new URL(value.trim()).origin.toLowerCase()
  } catch {
    return null
  }
}

const matchWildcardOrigin = (originUrl: URL, pattern: string): boolean => {
  const wildcardMatch = pattern.match(WILDCARD_ORIGIN_PATTERN)

  if (!wildcardMatch) return false

  const [, protocol, hostname, port = ''] = wildcardMatch

  if (originUrl.protocol !== `${protocol}:`) return false
  if (originUrl.port !== port) return false

  return originUrl.hostname !== hostname && originUrl.hostname.endsWith(`.${hostname}`)
}

export const matchOrigin = (origin: string, patterns: string[]): boolean => {
  if (patterns.length === 0) return false

  const normalizedOrigin = normalizeOrigin(origin)

  if (!normalizedOrigin) return false

  const originUrl = new URL(normalizedOrigin)

  return patterns.some((pattern) => {
    const normalizedPattern = pattern.trim().toLowerCase()

    if (!normalizedPattern) return false

    if (normalizedPattern.includes('*')) {
      return matchWildcardOrigin(originUrl, normalizedPattern)
    }

    return normalizeOrigin(normalizedPattern) === normalizedOrigin
  })
}
